/**
 * Phase 32 Plan 32-2 — admin unmapped-commits table per UI-SPEC §264.
 *
 * Lists GitHub logins seen in commits whose author could not be resolved to
 * a Monksheets user. Each row offers a "Map user →" deeplink to the admin
 * user search page pre-filled with the login (admin sets the user's
 * `githubUsername` to flip those commits via the Plan 32-1b reresolve hook).
 */
import type { ReactElement } from 'react';
import type { UnmappedRow } from '../../services/api/engineeringMetrics';
import { formatDate } from '../../utils/dateFormat';

export interface UnmappedCommitsTableProps {
  rows: UnmappedRow[];
}

export function UnmappedCommitsTable({ rows }: UnmappedCommitsTableProps): ReactElement {
  if (rows.length === 0) {
    return (
      <div className="bg-green-50 border border-green-200 rounded-lg p-6 text-center">
        <h3 className="text-base font-medium text-green-900">All commits are mapped</h3>
        <p className="mt-1 text-sm text-green-800">
          Every recent commit has been linked to a Monksheets user. Newly-onboarded
          engineers will appear here until their <code>githubUsername</code> is set.
        </p>
      </div>
    );
  }
  return (
    <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
      <table className="min-w-full divide-y divide-gray-200">
        <thead className="bg-gray-50">
          <tr>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              GitHub login
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              Commits
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              First seen
            </th>
            <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">
              Latest commit
            </th>
            <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">
              Action
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100 bg-white">
          {rows.map((row) => (
            <tr key={row.login}>
              <td className="px-4 py-3 text-sm font-mono text-gray-900">{row.login}</td>
              <td className="px-4 py-3 text-right text-sm text-gray-700">{row.count}</td>
              <td className="px-4 py-3 text-sm text-gray-600">{formatDate(row.firstSeenAt)}</td>
              <td className="px-4 py-3 text-sm text-gray-700 max-w-xs">
                <div className="text-xs text-gray-500">{formatDate(row.latestCommit.committedAt)}</div>
                <div className="truncate" title={row.latestCommit.messagePreview}>
                  {row.latestCommit.messagePreview}
                </div>
              </td>
              <td className="px-4 py-3 text-right text-sm">
                <a
                  href={`/admin/users?githubUsername=${encodeURIComponent(row.login)}`}
                  className="text-primary-600 hover:underline"
                >
                  Map user →
                </a>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default UnmappedCommitsTable;
